<template>
    <zen-grid-body-row
        :class="{ 'no-divide-bottom': showFullContext && hasContext }"
    >
        <!-- Expander -->
        <zen-grid-body-cell :hidable="false" class="align-top w-8 min-w-0">
            <button
                v-if="hasContext"
                @click="toggleShowFullContext"
                type="button"
                :aria-label="
                    showFullContext
                        ? 'Hide context details'
                        : 'Show context details'
                "
                :aria-expanded="showFullContext"
                class="cursor-pointer rounded-md p-0.5 text-taupe-400 hover:text-sky-600 hover:bg-sky-50 transition-colors"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="h-4 w-4 transition-transform"
                    :class="showFullContext ? 'rotate-90' : ''"
                >
                    <path
                        fill-rule="evenodd"
                        d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                        clip-rule="evenodd"
                    />
                </svg>
            </button>
            <span v-else />
        </zen-grid-body-cell>

        <!-- Date/Time -->
        <zen-grid-body-cell
            :hidable="false"
            :primary="true"
            class="align-top min-w-0"
        >
            <div class="text-sm font-medium text-taupe-900">
                <zen-date-time :value="result.dateTime" />
            </div>
            <div class="mt-1 font-mono text-xs text-taupe-400 break-all">
                {{ result.requestId }}
            </div>

            <!-- Mobile-only: Event + Context + Flag -->
            <div class="mt-2 space-y-2 lg:hidden">
                <div>
                    <span
                        class="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-600/20"
                    >
                        {{ formatEventType(result.eventType) }}
                    </span>
                </div>
                <div v-if="hasFlag" class="flex items-center gap-2 min-w-0">
                    <zen-inline-pill :value="result.flag?.type || 'string'" />
                    <span
                        class="font-mono text-sm text-taupe-900 whitespace-nowrap"
                    >
                        {{ result.flag?.key }}
                    </span>
                </div>
                <div v-if="hasContext" class="flex items-center gap-2 min-w-0">
                    <zen-inline-pill :value="result.context?.type" />
                    <span class="text-sm text-taupe-700 truncate min-w-0">
                        {{ result.context?.name }}
                    </span>
                </div>
                <div
                    v-if="showFullContext && hasContext"
                    class="rounded-lg bg-taupe-50 px-3 py-2 ring-1 ring-taupe-200/60"
                >
                    <p
                        class="text-[11px] font-medium uppercase tracking-wide text-taupe-400"
                    >
                        Attributes
                    </p>
                    <dl class="mt-1.5 divide-y divide-taupe-200/60">
                        <div
                            v-for="attr in contextAttributes"
                            :key="attr.key"
                            class="flex justify-between gap-4 py-1.5 first:pt-0 last:pb-0"
                        >
                            <dt
                                class="text-xs font-medium text-taupe-500 shrink-0"
                            >
                                {{ attr.key }}
                            </dt>
                            <dd
                                class="text-xs font-mono text-taupe-900 text-right break-all min-w-0"
                            >
                                {{ attr.value }}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </zen-grid-body-cell>

        <!-- Event Type -->
        <zen-grid-body-cell class="align-top min-w-0">
            <span
                class="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700 ring-1 ring-inset ring-sky-600/20"
            >
                {{ formatEventType(result.eventType) }}
            </span>
        </zen-grid-body-cell>

        <!-- Flag -->
        <zen-grid-body-cell class="align-top min-w-0">
            <div v-if="hasFlag" class="flex items-center gap-2 min-w-0">
                <zen-inline-pill :value="result.flag?.type || 'string'" />
                <span
                    class="font-mono text-sm text-taupe-900 whitespace-nowrap"
                >
                    {{ result.flag?.key }}
                </span>
            </div>
            <span v-else />
        </zen-grid-body-cell>
    </zen-grid-body-row>

    <!-- Expanded context row (desktop only; mobile uses inline context above) -->
    <tr
        v-if="showFullContext && hasContext"
        class="expanded-context-row hidden lg:table-row"
    >
        <td></td>
        <td colspan="3" class="px-3 pb-4 pt-0">
            <div
                class="rounded-lg bg-taupe-50 px-4 py-3 ring-1 ring-taupe-200/60"
            >
                <p
                    class="text-[11px] font-medium uppercase tracking-wide text-taupe-400 mb-2"
                >
                    Context
                </p>
                <div class="flex items-center gap-2 mb-3">
                    <zen-inline-pill :value="result.context?.type" />
                    <span class="text-sm font-medium text-taupe-700">{{
                        result.context?.name
                    }}</span>
                </div>
                <div v-if="contextAttributes.length">
                    <p
                        class="text-[11px] font-medium uppercase tracking-wide text-taupe-400"
                    >
                        Attributes
                    </p>
                    <dl class="mt-1.5 divide-y divide-taupe-200/60">
                        <div
                            v-for="attr in contextAttributes"
                            :key="attr.key"
                            class="flex justify-between gap-4 py-1.5 first:pt-0 last:pb-0"
                        >
                            <dt
                                class="text-xs font-medium text-taupe-500 shrink-0"
                            >
                                {{ attr.key }}
                            </dt>
                            <dd
                                class="text-xs font-mono text-taupe-900 text-right break-all min-w-0"
                            >
                                {{ attr.value }}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </td>
    </tr>
</template>

<script>
export default {
    name: 'DebugConsoleRow',
    data() {
        return {
            showFullContext: false,
        }
    },
    computed: {
        hasContext() {
            const type = this.result.context?.type
            const name = this.result.context?.name
            return (
                (type && type !== '(none)') ||
                (name && name !== '(none)') ||
                this.contextAttributes.length > 0
            )
        },
        hasFlag() {
            const key = this.result.flag?.key
            return (
                key &&
                key !== '(flag-used)' &&
                key !== '(unknown)' &&
                key !== '(flag-json)'
            )
        },
        contextAttributes() {
            const attrs = this.result.context?.attributes
            if (!attrs) return []

            // Handle array of {key, values:[{value}]} objects
            if (Array.isArray(attrs)) {
                return attrs.map((attr) => ({
                    key: attr.key,
                    value: (attr.values || []).map((v) => v.value).join(', '),
                }))
            }

            // Handle plain object
            return Object.entries(attrs).map(([key, value]) => ({
                key,
                value: this.formatAttributeValue(value),
            }))
        },
    },
    methods: {
        formatEventType(eventType) {
            if (!eventType) {
                return '(unknown)'
            }

            return String(eventType)
                .replaceAll('-', ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())
        },
        toggleShowFullContext() {
            this.showFullContext = !this.showFullContext
        },
        formatAttributeValue(value) {
            if (Array.isArray(value)) {
                return value.join(', ')
            }
            if (value !== null && typeof value === 'object') {
                return JSON.stringify(value)
            }
            return String(value)
        },
    },
    props: {
        result: {
            required: true,
            type: Object,
        },
    },
}
</script>

<style scoped>
.expanded-context-row {
    border-top-width: 0 !important;
    border-top-style: none !important;
    border-bottom: 1px solid var(--color-taupe-200) !important;
}
</style>

<style>
@media (min-width: 1024px) {
    tr.no-divide-bottom {
        border-bottom-width: 0 !important;
        border-bottom-style: none !important;
    }
}

/* Highlight context row when hovering main row */
tr.no-divide-bottom:hover + tr.expanded-context-row,
/* Highlight context row when hovering it directly */
tr.expanded-context-row:hover {
    background-color: var(--color-sky-50);
}

/* Highlight main row when hovering context row */
tr.no-divide-bottom:has(+ tr.expanded-context-row:hover) {
    background-color: var(--color-sky-50);
}
</style>
